import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormHelperText, InputLabel, TextField } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { z } from "zod";
import useApi, { httpClient } from "../../hooks/useApi";
import Constants from "../../utils/Constants";
import MESSAGES from "../../utils/Messages";
import ReceivablePayableTable from "../Table/ReceivablePayableTable";

const ReceivablePayableConfirmation = forwardRef(
  ({ targetData, postSaveHandler = () => {}, readOnly = false }, ref) => {
    // バリデーション定義
    const schema = z
      .object({
        id: z.string(),
        requestStatement: z
          .string()
          .max(
            Constants.MULTI_LINE_TEXT_MAX,
            MESSAGES.TEXT_MAX_ERROR(Constants.MULTI_LINE_TEXT_MAX)
          )
          .optional(),
        confirmationDetails: z
          .array(
            z
              .object({
                id: z.string(),
                confirmationLetterId: z.string(),
                kubun: z
                  .string()
                  .min(1, MESSAGES.REQUIRED_ERROR_WITH_NAME("債権・債務の別"))
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
                type: z
                  .string()
                  .min(
                    1,
                    MESSAGES.REQUIRED_ERROR_WITH_NAME("勘定科目または摘要")
                  )
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
                details1: z
                  .string()
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
                volume: z.number({
                  required_error: MESSAGES.REQUIRED_ERROR_WITH_NAME("金額"),
                  invalid_type_error: MESSAGES.INVALID_NuMBER,
                }),
                unit: z
                  .string()
                  .min(1, MESSAGES.REQUIRED_ERROR_WITH_NAME("単位"))
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
                details2: z
                  .string()
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
              })
              .passthrough()
          )
          .refine(
            // 「相違の有無」の必須チェック
            (details) => {
              // 確認先でない場合は必須チェックしない
              if (role !== Constants.ROLE.CONFIRMER) return true;
              // 確認先の場合は必須チェックを行う
              if (
                details?.some(
                  (row) =>
                    // 「false」はOKのため「!」演算子は使用できない
                    row.differencesExistence === "" ||
                    row.differencesExistence === null ||
                    row.differencesExistence === undefined
                )
              ) {
                return false;
              } else {
                return true;
              }
            },
            {
              message: MESSAGES.REQUIRED_ERROR_WITH_NAME("相違の有無"),
              path: [0, "differencesExistence"],
            }
          )
          .refine(
            // 「相違内容」の必須チェック
            (details) => {
              // 確認先でない場合は必須チェックしない
              if (role !== Constants.ROLE.CONFIRMER) return true;
              // 確認先の場合は必須チェックを行う
              if (
                details?.some(
                  (row) =>
                    // 「false」はOKのため「!」演算子は使用できない
                    row.differencesExistence &&
                    (row.differences1 === "" ||
                      row.differences1 === null ||
                      row.differences1 === undefined)
                )
              ) {
                return false;
              } else {
                return true;
              }
            },
            {
              message: MESSAGES.DIFFERENCES_REQUIRED,
              path: [0, "differences1"],
            }
          )
          .optional(),
        text1: z
          .string()
          .max(
            Constants.MULTI_LINE_TEXT_MAX,
            MESSAGES.TEXT_MAX_ERROR(Constants.MULTI_LINE_TEXT_MAX)
          )
          .optional(),
      })
      .passthrough();

    const {
      control,
      handleSubmit,
      getValues,
      reset,
      trigger,
      formState: { errors, isDirty, isSubmitting },
    } = useForm({
      mode: "onSubmit",
      resolver: zodResolver(schema),
    });

    const { connect } = useApi();

    // 初期化
    useEffect(() => {
      // フォームの初期値設定
      reset(targetData);
    }, [reset, targetData]);

    // API POST
    const onSubmit = async (data) => {
      const result = await connect(() => {
        return httpClient.post(`/api/${role}/UpsertConfirmation`, data);
      }, "保存しました");
      if (result?.status === 200) {
        postSaveHandler();
      }
      console.log("post end", data);
    };

    // 親コンポーネントへの関数移譲
    useImperativeHandle(ref, () => ({
      // 親コンポーネントからフォームの値を参照可能にする
      getFormData: () => {
        return getValues();
      },
      // 親コンポーネントからバリデーション発火を可能にする
      validate: async () => {
        const isValid = await trigger();
        return isValid;
      },
      // 親コンポーネントからisDirtyを取得する
      getIsDirty: () => isDirty,
      // 親コンポーネントからisDirtyをリセットする
      resetIsDirty: () => {
        reset(getValues(), {
          keepDirty: false,
          keepTouched: false,
        });
      },
    }));
    const role = useSelector((state) => state.role.role);
    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          {role !== Constants.ROLE.CONFIRMER && (
            <Controller
              name="requestStatement"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  label="確認依頼文"
                  {...field}
                  multiline
                  fullWidth
                  disabled={role === Constants.ROLE.CLIENT || readOnly}
                  minRows={2}
                  sx={{ marginBottom: "1rem" }}
                  error={!!errors.differences}
                  helperText={
                    errors.differences ? errors.differences.message : ""
                  }
                />
              )}
            />
          )}
          <InputLabel>債権債務一覧</InputLabel>
          <Controller
            name="confirmationDetails"
            control={control}
            render={({ field }) => (
              <ReceivablePayableTable
                companyId={targetData?.companyId}
                confirmationLetterId={targetData?.id}
                value={field.value}
                onChange={field.onChange}
                saveHandler={handleSubmit(onSubmit)}
                postSaveHandler={postSaveHandler}
                readOnly={readOnly}
              />
            )}
          />
          <FormHelperText className="Mui-error">
            {errors?.confirmationDetails?.map((row) => {
              let errorFields = Object.keys(row);
              return (
                <>
                  {errorFields?.map((key) => (
                    <>
                      {row[key]?.message}
                      <br />
                    </>
                  ))}
                </>
              );
            })}
          </FormHelperText>
          <br />
          <Controller
            name="text1"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                fullWidth
                placeholder="補足がある場合はこちらにご記入ください（任意）"
                disabled={
                  role === Constants.ROLE.CLIENT ||
                  (role === Constants.ROLE.AUDITOR &&
                    targetData?.sendingMethod ===
                      Constants.SENDING_METHOD.EMAIL_OR_POST) ||
                  readOnly
                }
                minRows={2}
                error={!!errors.text1}
                helperText={errors.text1 ? errors.text1.message : ""}
              />
            )}
          />
          {!readOnly && role !== Constants.ROLE.CONFIRMER && (
            <Button type="submit" disabled={isSubmitting}>
              保存
            </Button>
          )}
        </form>
      </>
    );
  }
);

export default ReceivablePayableConfirmation;
